import { useCallback, useEffect, useRef } from 'react';

/** Fetch the next page when within this many viewports of the bottom. */
const SCROLL_THRESHOLD_VIEWPORTS = 2;
/** Minimum threshold so auto-fill triggers even when clientHeight is 0 at mount. */
const SCROLL_THRESHOLD_MIN_PX = 1500;

export interface UseInfiniteScrollOptions {
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
}

export interface UseInfiniteScrollReturn {
    /** Attach to the scroll container element. */
    containerRef: (node: HTMLElement | null) => void;
    /** Call from the container's onScroll handler. */
    onScroll: (e: React.UIEvent<HTMLElement>) => void;
}

/**
 * Fires `fetchNextPage` when the scroll container is within
 * {@link SCROLL_THRESHOLD_VIEWPORTS} viewports of the bottom, adapting
 * automatically to the container's visible height. Uses the onScroll event
 * pattern (TanStack Table official approach) instead of IntersectionObserver
 * for more reliable fast-scroll detection. Auto-fills the viewport on mount
 * and after each fetch.
 */
export const useInfiniteScroll = ({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
    const containerEl = useRef<HTMLElement | null>(null);

    const stateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
    stateRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };

    const checkAndFetch = useCallback((el?: HTMLElement | null) => {
        if (!el) return;
        const { scrollHeight, scrollTop, clientHeight } = el;
        const { hasNextPage: hn, isFetchingNextPage: fn, fetchNextPage: fnp } = stateRef.current;
        const threshold = Math.max(
            clientHeight * SCROLL_THRESHOLD_VIEWPORTS,
            SCROLL_THRESHOLD_MIN_PX,
        );
        if (scrollHeight - scrollTop - clientHeight < threshold && hn && !fn) {
            fnp();
        }
    }, []);

    const containerRef = useCallback(
        (node: HTMLElement | null) => {
            containerEl.current = node;
            checkAndFetch(node);
        },
        [checkAndFetch],
    );

    const onScroll = useCallback(
        (e: React.UIEvent<HTMLElement>) => {
            checkAndFetch(e.currentTarget);
        },
        [checkAndFetch],
    );

    // Auto-fill: after each fetch completes (isFetchingNextPage flips false),
    // re-check if the viewport still isn't full and fetch again if needed.
    useEffect(() => {
        if (!isFetchingNextPage && hasNextPage) {
            checkAndFetch(containerEl.current);
        }
    }, [isFetchingNextPage, hasNextPage, checkAndFetch]);

    return { containerRef, onScroll };
};
